<template>
    <div>
        <kgr-member-contract-data-provider
            v-slot="contractScope"
            :member-id="memberId"
            :callback="onMemberContractsLoaded"
        >
            <div>
                <kgr-form-scaffold
                    :pending="pending"
                    edit-mode
                    :save-button-disabled="!isEditable || !formIsValid"
                    @primaryBtnClicked="onUpdate(contractScope.reload)"
                    @backBtnClicked="redirectToOverview"
                >
                    <table class="kgr-table kgr-table--striped table-groups kgr-row kgr-table--block">
                        <thead>
                            <tr class="kgr-table__row">
                                <th
                                    v-t="'memberContract.membershipNumber'"
                                    class="kgr-table__cell kgr-table__cell--headline"
                                ></th>
                                <th
                                    v-t="'Vertragsart'"
                                    class="kgr-table__cell kgr-table__cell--headline"
                                ></th>
                                <th
                                    v-t="'Eintrittsdatum'"
                                    class="kgr-table__cell kgr-table__cell--headline"
                                ></th>
                                <th
                                    v-t="'memberContract.end'"
                                    class="kgr-table__cell kgr-table__cell--headline"
                                ></th>
                                <th
                                    v-t="'memberContract.terminationReason'"
                                    class="kgr-table__cell kgr-table__cell--headline"
                                ></th>
                                <th
                                    v-if="isEditable"
                                    class="kgr-table__cell kgr-table__cell--headline"
                                ></th>
                            </tr>
                        </thead>
                        <tbody>
                            <kgr-member-contract-data-form
                                v-for="(memberContract,index) in memberContracts"
                                :key="memberContract.id"
                                v-model="memberContracts[index]"
                                :member-id="memberId"
                                :club-id="clubId"
                                :error="error.errorAtIndex('contracts', index)"
                                class="animated fadeIn"
                                :is-editable="isEditable"
                                @enter="onUpdate(contractScope.reload)"
                                @delete="deleteContract"
                                @undelete="undeleteContract"
                            />
                        </tbody>
                    </table>
                    <kgr-button
                        v-if="isEditable"
                        :disabled="newContractAdded"
                        @click="addRegularMemberContract()"
                        v-text="$t('Neuer Vertrag')"
                    />
                    <kgr-button
                        v-if="isEditable"
                        :disabled="newContractAdded"
                        @click="addGuestContract()"
                        v-text="$t('Neuer Gastvertrag')"
                    />
                    <kgr-button
                        v-if="isEditable"
                        :disabled="newContractAdded"
                        @click="addPassiveContract()"
                        v-text="$t('Neuer Passivvertrag')"
                    />
                </kgr-form-scaffold>
            </div>
        </kgr-member-contract-data-provider>
    </div>
</template>

<script>
import Error from '../../js/model/Error';
import FormScaffold from '../../components/common/form/FormScaffold';
import KgrButton from '../../components/common/form/button/KgrButton';
import MemberContractDataForm from '../../components/members/MemberContractDataForm';
import MemberContractDataProvider from '../../components/data-provider/MemberContractDataProvider';
import MemberGuestContract from '../../js/dto/MemberGuestContract';
import MemberPassiveContract from '../../js/dto/MemberPassiveContract';
import MemberRegularContract from '../../js/dto/MemberRegularContract';
import MemberRepository from '../../js/repository/MemberRepository';

export default {
    components: {
        'kgr-member-contract-data-provider': MemberContractDataProvider,
        'kgr-member-contract-data-form': MemberContractDataForm,
        'kgr-form-scaffold': FormScaffold,
        'kgr-button': KgrButton,
    },
    props: {
        overviewUrl: {
            type: String,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: false,
            default: false,
        },
        recycleMembershipNumbers: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            pending: false,
            error: new Error(),
            memberContracts: [],
        };
    },
    computed: {
        newContractAdded() {
            return this.memberContracts.find(memberContract => memberContract.id === null) != null;
        },
        formIsValid() {
            return this.memberContracts.find(memberContract => memberContract.start === null || memberContract.invalidChanges.length > 0) == null;
        }
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        onMemberContractsLoaded(loadedMemberContracts) {
            this.memberContracts = loadedMemberContracts;
        },
        onUpdate(contractsReload) {
            this.updateMemberContracts(contractsReload);
        },
        updateMemberContracts(reload) {
            if(!this.isEditable || !this.formIsValid) {
                return;
            }
            this.pending = true;
            let notDeletedContracts = this.memberContracts.filter(contract => !contract.deleted);
            MemberRepository.updateContracts(this.memberId, notDeletedContracts)
                .then(() => {
                    this.error = new Error();
                    reload();
                    this.$notify.success('saveChanges.success');
                    this.pending = false;
                    this.memberContracts = notDeletedContracts;
                })
                .catch(error => {
                    this.error = error;
                    if (error.forField('contracts')) {
                        this.$notify.error(error.messageForField('contracts'));
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                    this.pending = false;
                });
        },
        addGuestContract() {
            this.error = new Error()
            let contract = new MemberGuestContract();
            contract.memberId = this.memberId;
            this.memberContracts.unshift(contract)
        },
        addPassiveContract() {
            this.error = new Error()
            let contract = new MemberPassiveContract();
            if(this.recycleMembershipNumbers) {
                contract.membershipNumber = this.cloneLatestMembershipNumber() || contract.membershipNumber;
            }
            contract.memberId = this.memberId;
            this.memberContracts.unshift(contract)
        },
        addRegularMemberContract() {
            this.error = new Error()
            let contract = new MemberRegularContract();
            if(this.recycleMembershipNumbers) {
                contract.membershipNumber = this.cloneLatestMembershipNumber() || contract.membershipNumber;
            }
            contract.memberId = this.memberId;
            this.memberContracts.unshift(contract)
        },
        deleteContract(memberContract) {
            this.error = new Error()
            if(memberContract.isNew()) {
                this.memberContracts.splice(this.memberContracts.indexOf(memberContract), 1);
            }
        },
        undeleteContract() {
            // do nothing so far
        },

        cloneLatestMembershipNumber() {
            const numberedContracts = this.memberContracts.filter(contract => contract instanceof MemberPassiveContract || contract instanceof MemberRegularContract);
            if(numberedContracts.length > 0) {
                return numberedContracts[0].membershipNumber.clone();
            }
        },
    },

}
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
